// RiderTrackingMap.js - Real-time rider location tracking for 3PL partners

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { Spinner, ListGroup as BSListGroup } from "react-bootstrap";
import socket from "../../utilities/socket";
import RiderFleetMap from "../../components/Maps/RiderFleetMap";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../../utilities/user";
import {
  FaMapMarkedAlt,
  FaBicycle,
  FaMotorcycle,
  FaCar,
  FaTruck,
  FaShuttleVan,
} from "react-icons/fa";

// Colors
const colors = {
  blue: "#164773",
  green: "#10b981",
  yellow: "#f59e0b",
  red: "#ef4444",
  gray: "#6b7280",
  white: "#ffffff",
  darkBg: "#0f1923",
  cardBg: "#1a2636",
  border: "#243447",
};

// Injected CSS for pulsing markers and map chrome
const MAP_CSS = `
  .fm-wrap { position: relative; height: 100%; }
  .fm-overlay { position: absolute; top: 12px; left: 50%; transform: translateX(-50%); z-index: 1000; display: flex; gap: 8px; pointer-events: none; }
  .fm-stat-pill { background: rgba(15,25,35,0.88); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 5px 14px; display: flex; align-items: center; gap: 7px; color: #fff; font-size: 0.72rem; font-weight: 700; white-space: nowrap; }
  .fm-stat-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .fm-stat-dot.green  { background: #10b981; box-shadow: 0 0 6px #10b981; }
  .fm-stat-dot.yellow { background: #f59e0b; box-shadow: 0 0 6px #f59e0b; }
  .fm-stat-dot.gray   { background: #6b7280; }
  .fm-stat-dot.blue   { background: #5bc5f5; box-shadow: 0 0 6px #5bc5f5; }
  .fm-live-badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(15,25,35,0.88); backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 5px 12px; color: #fff; font-size: 0.72rem; font-weight: 700; }
  .fm-live-dot { width: 8px; height: 8px; border-radius: 50%; background: #10b981; box-shadow: 0 0 0 0 rgba(16,185,129,0.7); animation: fm-pulse 1.8s infinite; }
  .fm-live-dot.off { background: #ef4444; box-shadow: none; animation: none; }
  @keyframes fm-pulse { 0%{box-shadow:0 0 0 0 rgba(16,185,129,0.7)} 70%{box-shadow:0 0 0 8px rgba(16,185,129,0)} 100%{box-shadow:0 0 0 0 rgba(16,185,129,0)} }
  @keyframes fm-ring-pulse { 0%{transform:scale(1);opacity:0.8} 100%{transform:scale(2.4);opacity:0} }
  .rider-pulse-ring { animation: fm-ring-pulse 1.6s ease-out infinite; }
  .fm-popup { min-width: 200px; font-family: inherit; }
  .fm-popup-name { font-weight: 800; font-size: 0.92rem; color: #0f1923; margin-bottom: 2px; }
  .fm-popup-carrier { font-size: 0.72rem; color: #6b7280; margin-bottom: 8px; }
  .fm-popup-row { display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; padding: 3px 0; border-bottom: 1px solid #f0f4f8; }
  .fm-popup-row:last-child { border-bottom: none; }
  .fm-popup-label { color: #8a9ab0; font-weight: 600; }
  .fm-popup-val { font-weight: 700; color: #1a2636; }
  .fm-popup-status-online  { color: #10b981; }
  .fm-popup-status-offline { color: #6b7280; }
  .fm-popup-status-busy    { color: #f59e0b; }
  .fm-rider-item { padding: 12px 14px; border-left: 4px solid transparent; transition: background 0.15s; cursor: pointer; border-bottom: 1px solid #f0f4f8; }
  .fm-rider-item:hover { background: #f0f9ff; }
  .fm-rider-item.selected { background: #e8f4fd; border-left-color: #164773; }
  .fm-rider-item.online  { border-left-color: #10b981; }
  .fm-rider-item.offline { border-left-color: #d1d5db; }
  .fm-rider-item.busy    { border-left-color: #f59e0b; }
  .fm-avatar { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 0.78rem; color: #fff; flex-shrink: 0; }
  .fm-rider-name { font-weight: 700; font-size: 0.84rem; color: #1a2636; line-height: 1.2; }
  .fm-rider-sub  { font-size: 0.7rem; color: #9aacbf; margin-top: 1px; }
  .fm-rider-loc  { font-size: 0.7rem; color: #164773; font-weight: 600; margin-top: 4px; }
  .fm-badge { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
  .fm-badge-online  { background: #2df30f; color: #012709; }
  .fm-badge-offline { background: #f3f4f6; color: #4b5563; }
  .fm-badge-busy    { background: #fef3c7; color: #92400e; }
  .fm-badge-order   { background: #fef3c7; color: #92400e; margin-top: 3px; }
  .fm-panel-header  { background: linear-gradient(135deg, #0f1923 0%, #164773 100%); padding: 14px 18px; color: #fff; display: flex; align-items: center; justify-content: space-between; }
  .fm-panel-title   { font-weight: 800; font-size: 0.9rem; letter-spacing: 0.04em; display: flex; align-items: center; gap: 8px; }
  .fm-map-header    { background: linear-gradient(135deg, #0f1923 0%, #164773 100%); padding: 12px 18px; color: #fff; display: flex; align-items: center; justify-content: space-between; }
  .fm-map-title     { font-weight: 800; font-size: 0.9rem; letter-spacing: 0.04em; }
  .fm-map-updated   { font-size: 0.68rem; color: rgba(255,255,255,0.55); margin-top: 2px; }
  .gm-style .gm-style-iw-c { border-radius: 10px !important; box-shadow: 0 8px 24px rgba(0,0,0,0.18) !important; padding: 12px !important; }
  .gm-style .gm-style-iw-tc { display: none !important; }
  .rider-marker { transition: transform 0.4s linear; }
`;

// Helper to extract lat/lng from GeoJSON or legacy format
const getLatLng = (location) => {
  if (!location) return null;
  // GeoJSON format: { type: 'Point', coordinates: [lng, lat] }
  if (location.coordinates && location.coordinates.length === 2) {
    return { lat: location.coordinates[1], lng: location.coordinates[0] };
  }
  // Legacy format: { lat, lng }
  if (location.lat !== undefined && location.lng !== undefined) {
    return { lat: location.lat, lng: location.lng };
  }
  return null;
};

// ── Rider presenters (shared by the side panel and the map popup) ────────────
// Avatar initials helper
const initials = (r) => {
  const f = r.firstName?.[0] || "";
  const l = r.lastName?.[0] || "";
  return (f + l).toUpperCase() || "?";
};

const avatarColor = (r) =>
  r.onlineStatus === "Online"
    ? r.currentActiveOrders > 0
      ? colors.yellow
      : colors.green
    : colors.gray;

const carrierIcon = (type) => {
  const s = { verticalAlign: "-2px", marginRight: 2 };
  switch ((type || "").toLowerCase()) {
    case "bicycle":
      return <FaBicycle style={s} />;
    case "motorcycle":
      return <FaMotorcycle style={s} />;
    case "car":
      return <FaCar style={s} />;
    case "truck":
      return <FaTruck style={s} />;
    case "van":
      return <FaShuttleVan style={s} />;
    default:
      return <FaMotorcycle style={s} />;
  }
};

const statusClass = (s) =>
  s === "Online" ? "online" : s === "Busy" ? "busy" : "offline";


// Position accessor handed to RiderFleetMap.
const getRiderPosition = (rider) => getLatLng(rider.currentLocation);

const RiderTrackingMap = () => {
  const { agent } = useSelector((state) => state.auth);
  // Compute stable auth values — prefer Redux agent but fall back to localStorage
  // immediately so partnerId is never undefined on first mount (fixes need-to-refresh bug)
  const localUser = getUserFromLocalStorage();
  const user = agent || localUser;
  const partnerId = user?.result?.partnerId || user?.result?._id;
  const token = user?.token;

  // Keep a ref so the socket effect can always see the latest partnerId
  const partnerIdRef = useRef(partnerId);
  useEffect(() => {
    partnerIdRef.current = partnerId;
  }, [partnerId]);

  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(socket.connected);
  const [selectedRider, setSelectedRider] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [locationNames, setLocationNames] = useState({}); // Cache for reverse geocoded names
  const locationNamesRef = useRef({}); // Ref mirror — lets reverseGeocode read cache without stale closure
  const [partnerLogo, setPartnerLogo] = useState(null); // Partner logo for map markers
  const [riderTrails, setRiderTrails] = useState({}); // Recent positions per rider for trail polyline
  const MAX_TRAIL_POINTS = 20; // Keep last 20 GPS pings

  const API_URL = process.env.REACT_APP_SERVER_URL;

  // Fetch partner profile to get logo
  useEffect(() => {
    const fetchPartnerLogo = async () => {
      if (!partnerId || !token) return;
      try {
        const response = await fetch(
          `${API_URL}/api/3pl-partners/${partnerId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await response.json();
        if (data.success && data.data?.partner?.logo?.url) {
          setPartnerLogo(data.data.partner.logo.url);
        } else if (data.data?.logo?.url) {
          setPartnerLogo(data.data.logo.url);
        }
      } catch (error) {
        console.warn("Could not fetch partner logo:", error);
      }
    };
    fetchPartnerLogo();
  }, [partnerId, token, API_URL]);

  /**
   * Reverse geocode coordinates to a human-readable location name.
   *
   * Strategy (confirmed against live Google API payloads for Uganda):
   * 1. Google Places Nearby Search (50m radius) — returns `name` field with
   *    actual business/venue names e.g. "Egreet Pilgrims Inn". Geocoding API
   *    does NOT return business names, only road/sublocality.
   * 2. If no nearby place found, fall back to Google Geocoding API for the
   *    best available road + neighbourhood label.
   */
  const reverseGeocode = useCallback(
    async (lat, lng) => {
      const cacheKey = `${lat.toFixed(5)},${lng.toFixed(5)}`;

      // Check ref first — avoids stale closure on locationNames state
      if (locationNamesRef.current[cacheKey]) {
        return locationNamesRef.current[cacheKey];
      }

      try {
        // ── Step 1: Places Nearby Search via backend proxy (avoids CORS) ──
        const nearbyRes = await fetch(
          `${API_URL}/api/places/nearby?lat=${lat}&lng=${lng}`,
        );
        if (nearbyRes.ok) {
          const nearbyData = await nearbyRes.json();
          const estTypes = [
            "establishment",
            "point_of_interest",
            "lodging",
            "food",
            "store",
            "health",
            "school",
            "place_of_worship",
            "premise",
            "restaurant",
            "cafe",
            "bar",
            "hospital",
            "pharmacy",
          ];

          // Filter to actual establishments — skip pure locality/political results
          const establishments = (nearbyData.results || []).filter((r) =>
            r.types.some((t) => estTypes.includes(t)),
          );

          // Pick the closest establishment by straight-line distance
          let place = null;
          let minDist = Infinity;
          for (const r of establishments) {
            const rLat = r.geometry?.location?.lat;
            const rLng = r.geometry?.location?.lng;
            if (rLat != null && rLng != null) {
              const d = Math.pow(rLat - lat, 2) + Math.pow(rLng - lng, 2);
              if (d < minDist) {
                minDist = d;
                place = r;
              }
            }
          }

          if (place?.name) {
            const label = place.vicinity
              ? `${place.name}, ${place.vicinity.split(",")[0]}`
              : place.name;
            locationNamesRef.current[cacheKey] = label;
            setLocationNames((prev) => ({ ...prev, [cacheKey]: label }));
            return label;
          }

          // No establishment — use neighborhood/route/sublocality from same response
          const results = nearbyData.results || [];
          const neighborhood = results.find((r) =>
            r.types.some((t) =>
              [
                "neighborhood",
                "sublocality",
                "sublocality_level_1",
                "route",
              ].includes(t),
            ),
          );
          if (neighborhood?.name) {
            const fallback =
              neighborhood.vicinity &&
              neighborhood.vicinity !== neighborhood.name
                ? `${neighborhood.name}, ${neighborhood.vicinity.split(",")[0]}`
                : neighborhood.name;
            locationNamesRef.current[cacheKey] = fallback;
            setLocationNames((prev) => ({ ...prev, [cacheKey]: fallback }));
            return fallback;
          }

          // Last resort — use first non-locality result or the locality itself
          const any = results.find(
            (r) =>
              !r.types.includes("country") &&
              !r.types.includes("administrative_area_level_1"),
          );
          if (any?.name) {
            locationNamesRef.current[cacheKey] = any.name;
            setLocationNames((prev) => ({ ...prev, [cacheKey]: any.name }));
            return any.name;
          }
        }

        // ── Step 2: Geocoding fallback via backend proxy ──
        try {
          const geoRes = await fetch(
            `${API_URL}/api/places/geocode?lat=${lat}&lng=${lng}`,
          );
          if (geoRes.ok) {
            const geoData = await geoRes.json();
            if (geoData.status === "OK" && geoData.results?.length) {
              const getComp = (result, type) =>
                result.address_components?.find((c) => c.types.includes(type))
                  ?.long_name || "";

              let label = "";
              for (const result of geoData.results) {
                const road = getComp(result, "route");
                const sub =
                  getComp(result, "sublocality_level_1") ||
                  getComp(result, "sublocality") ||
                  getComp(result, "neighborhood");
                const locality = getComp(result, "locality");
                if (road) {
                  label = sub
                    ? `${road}, ${sub}`
                    : locality
                    ? `${road}, ${locality}`
                    : road;
                  break;
                }
                if (sub) {
                  label = sub;
                  break;
                }
              }

              if (!label && geoData.results[0]) {
                label = geoData.results[0].formatted_address
                  .split(",")
                  .slice(0, 2)
                  .join(",")
                  .trim();
              }

              if (label) {
                locationNamesRef.current[cacheKey] = label;
                setLocationNames((prev) => ({ ...prev, [cacheKey]: label }));
                return label;
              }
            }
          }
        } catch (geoErr) {
          console.warn("Geocoding fallback failed:", geoErr);
        }

        const resolved = "Unknown location";
        locationNamesRef.current[cacheKey] = resolved;
        setLocationNames((prev) => ({ ...prev, [cacheKey]: resolved }));
        return resolved;
      } catch (error) {
        console.error("Reverse geocoding error:", error);
        return null;
      }
    },
    [API_URL],
  );

  // Fetch location names for all online riders
  useEffect(() => {
    const fetchLocationNames = async () => {
      for (const rider of riders) {
        const loc = getLatLng(rider.currentLocation);
        if (loc && loc.lat !== 0 && loc.lng !== 0) {
          await reverseGeocode(loc.lat, loc.lng);
        }
      }
    };

    if (riders.length > 0) {
      fetchLocationNames();
    }
  }, [riders, reverseGeocode]);

  // Fetch initial rider data
  const fetchRiders = useCallback(async () => {
    if (!partnerId || !token) {
      console.warn("No partnerId or token found, skipping fetch");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/3pl-partners/${partnerId}/riders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      if (data.success) {
        setRiders(data.data?.riders || []);
      } else {
        console.error("Failed to fetch riders:", data);
      }
    } catch (error) {
      console.error("Error fetching riders:", error);
      toast.error("Failed to load riders");
    } finally {
      setLoading(false);
    }
  }, [partnerId, token, API_URL]);

  // Socket connection and event handlers
  useEffect(() => {
    if (!partnerId) {
      setLoading(false);
      return;
    }

    const handleConnect = () => {
      setConnected(true);
      socket.emit("join3PLPartnerRoom", partnerIdRef.current);
    };

    const handleDisconnect = () => setConnected(false);

    const handleLocationUpdate = (data) => {
      setRiders((prev) =>
        prev.map((rider) =>
          rider._id === data.riderId
            ? {
                ...rider,
                currentLocation: {
                  lat: data.location.lat,
                  lng: data.location.lng,
                  heading: data.location.heading,
                  speed: data.location.speed,
                  lastUpdated: data.timestamp,
                },
              }
            : rider,
        ),
      );
      // Append to trail — keep last MAX_TRAIL_POINTS positions
      setRiderTrails((prev) => {
        const existing = prev[data.riderId] || [];
        const updated = [...existing, [data.location.lat, data.location.lng]];
        return { ...prev, [data.riderId]: updated.slice(-MAX_TRAIL_POINTS) };
      });
      setLastUpdate(new Date());
    };

    const handleStatusChange = (data) => {
      setRiders((prev) =>
        prev.map((rider) =>
          rider._id === data.riderId
            ? {
                ...rider,
                onlineStatus: data.status,
                availability: {
                  ...rider.availability,
                  isAvailable: data.isAvailable,
                },
              }
            : rider,
        ),
      );
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("rider:locationUpdated", handleLocationUpdate);
    socket.on("rider:statusChanged", handleStatusChange);

    // Join room immediately — handles both already-connected and reconnect cases
    if (socket.connected) {
      setConnected(true);
      socket.emit("join3PLPartnerRoom", partnerId);
    } else {
      // Socket not yet connected — attempt to connect so the 'connect' handler fires
      socket.connect();
    }

    // Always fetch initial rider data regardless of socket state
    fetchRiders();

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("rider:locationUpdated", handleLocationUpdate);
      socket.off("rider:statusChanged", handleStatusChange);
      socket.emit("leave3PLPartnerRoom", partnerId);
    };
  }, [partnerId, fetchRiders]);

  // Filter riders with location data
  const ridersWithLocation = riders.filter((r) => getLatLng(r.currentLocation));

  const onlineRiders = riders.filter((r) => r.onlineStatus === "Online");
  const offlineRiders = riders.filter((r) => r.onlineStatus !== "Online");

  // Default center (Kampala, Uganda) and Uganda bounds

  // ── Marker popup ────────────────────────────────────────────────────────────
  // Handed to RiderFleetMap, which renders it into the Google info window and
  // re-renders it while the window is open so speed and last ping stay live.
  const renderRiderPopup = useCallback(
    (rider) => {
      const loc = getRiderPosition(rider);
      const cacheKey = loc
        ? `${loc.lat.toFixed(5)},${loc.lng.toFixed(5)}`
        : null;
      const locName = cacheKey ? locationNames[cacheKey] : null;
      const speed = rider.currentLocation?.speed;
      const updatedAt = rider.currentLocation?.lastUpdated;
      const statusCls = statusClass(rider.onlineStatus);

      return (
        <div className="fm-popup">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div
              className="fm-avatar"
              style={{
                background: avatarColor(rider),
                width: 38,
                height: 38,
                fontSize: "0.8rem",
              }}
            >
              {initials(rider)}
            </div>
            <div>
              <div className="fm-popup-name">
                {rider.firstName} {rider.lastName}
              </div>
              <div className="fm-popup-carrier">
                {carrierIcon(rider.carrierType)} {rider.carrierType}
              </div>
            </div>
          </div>
          <div className="fm-popup-row">
            <span className="fm-popup-label">Status</span>
            <span className={`fm-popup-val fm-popup-status-${statusCls}`}>
              {rider.onlineStatus}
            </span>
          </div>
          <div className="fm-popup-row">
            <span className="fm-popup-label">Active Orders</span>
            <span className="fm-popup-val">
              {rider.currentActiveOrders || 0}
            </span>
          </div>
          {speed > 0 && (
            <div className="fm-popup-row">
              <span className="fm-popup-label">Speed</span>
              <span className="fm-popup-val">{Math.round(speed)} km/h</span>
            </div>
          )}
          {locName && (
            <div className="fm-popup-row">
              <span className="fm-popup-label">Location</span>
              <span
                className="fm-popup-val"
                style={{ maxWidth: 120, textAlign: "right" }}
              >
                {locName}
              </span>
            </div>
          )}
          {updatedAt && (
            <div
              style={{
                marginTop: 6,
                fontSize: "0.65rem",
                color: "#9aacbf",
                textAlign: "right",
              }}
            >
              Pinged {new Date(updatedAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      );
    },
    [locationNames],
  );

  if (loading) {
    return (
      <div
        className="content d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="grow" style={{ color: colors.blue }} />
      </div>
    );
  }

  return (
    <div className="content">
      <style>{MAP_CSS}</style>

      {/* Page header */}
      <Row className="mb-3" style={{ alignItems: "center" }}>
        <Col>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "linear-gradient(135deg,#0f1923,#164773)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 20 }}>🛵</span>
            </div>
            <div>
              <h4
                style={{
                  margin: 0,
                  color: colors.blue,
                  fontWeight: 800,
                  fontSize: "1.15rem",
                }}
              >
                Live Rider Tracking
              </h4>
              <p style={{ margin: 0, color: colors.gray, fontSize: "0.78rem" }}>
                Real-time location of your delivery fleet
              </p>
            </div>
          </div>
        </Col>
        <Col className="text-end">
          <span className="fm-live-badge">
            <span className={`fm-live-dot${connected ? "" : " off"}`} />
            {connected ? "Live" : "Disconnected"}
          </span>
          {lastUpdate && (
            <div
              style={{ color: colors.gray, fontSize: "0.68rem", marginTop: 4 }}
            >
              Updated {lastUpdate.toLocaleTimeString()}
            </div>
          )}
        </Col>
      </Row>

      <Row>
        {/* Map panel */}
        <Col lg="8" style={{ marginBottom: 20 }}>
          <Card
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: "none",
              boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
            }}
          >
            <div className="fm-map-header">
              <div>
                <div
                  className="fm-map-title"
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <FaMapMarkedAlt style={{ fontSize: 25, opacity: 0.85 }} />{" "}
                  Fleet Map
                </div>
                {lastUpdate && (
                  <div className="fm-map-updated">
                    Last ping: {lastUpdate.toLocaleTimeString()}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <span className="fm-stat-pill">
                  <span className="fm-stat-dot blue" />
                  {riders.length} Total
                </span>
                <span className="fm-stat-pill">
                  <span className="fm-stat-dot green" />
                  {onlineRiders.length} Online
                </span>
                <span className="fm-stat-pill">
                  <span className="fm-stat-dot gray" />
                  {offlineRiders.length} Offline
                </span>
              </div>
            </div>
            <div style={{ position: "relative", height: 520 }}>
              <RiderFleetMap
                riders={ridersWithLocation}
                getPosition={getRiderPosition}
                trails={riderTrails}
                selectedRiderId={selectedRider?._id || null}
                onSelectRider={setSelectedRider}
                renderPopup={renderRiderPopup}
                partnerLogo={partnerLogo}
                height="100%"
              />

              {/* Floating location count pill */}
              <div
                style={{
                  position: "absolute",
                  bottom: 14,
                  left: 14,
                  zIndex: 1000,
                }}
              >
                <span className="fm-stat-pill">
                  <span className="fm-stat-dot yellow" />
                  {ridersWithLocation.length} with live location
                </span>
              </div>
            </div>
          </Card>
        </Col>

        {/* Rider list panel */}
        <Col lg="4" style={{ marginBottom: 20 }}>
          <Card
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: "none",
              boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
              height: 598,
            }}
          >
            <div className="fm-panel-header">
              <div className="fm-panel-title">
                <span>👥</span> Riders
                <span
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: 20,
                    padding: "1px 10px",
                    fontSize: "0.72rem",
                  }}
                >
                  {riders.length}
                </span>
              </div>
              <div style={{ display: "flex", gap: 5 }}>
                <span
                  style={{
                    fontSize: "0.65rem",
                    background: "#10b91b",
                    color: "#fff",
                    borderRadius: 20,
                    padding: "2px 8px",
                    fontWeight: 700,
                  }}
                >
                  {onlineRiders.length} Online
                </span>
                <span
                  style={{
                    fontSize: "0.65rem",
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    borderRadius: 20,
                    padding: "2px 8px",
                    fontWeight: 700,
                  }}
                >
                  {offlineRiders.length} Offline
                </span>
              </div>
            </div>
            <div
              style={{
                overflowY: "auto",
                flex: 1,
                height: "calc(100% - 54px)",
              }}
            >
              {riders.length === 0 ? (
                <div
                  style={{
                    padding: 40,
                    textAlign: "center",
                    color: colors.gray,
                    fontSize: "0.82rem",
                  }}
                >
                  No riders found
                </div>
              ) : (
                riders.map((rider) => {
                  const loc = getLatLng(rider.currentLocation);
                  const cacheKey = loc
                    ? `${loc.lat.toFixed(5)},${loc.lng.toFixed(5)}`
                    : null;
                  const locName = cacheKey ? locationNames[cacheKey] : null;
                  const sCls = statusClass(rider.onlineStatus);
                  const isSelected = selectedRider?._id === rider._id;
                  return (
                    <div
                      key={rider._id}
                      className={`fm-rider-item ${sCls}${
                        isSelected ? " selected" : ""
                      }`}
                      onClick={() => setSelectedRider(rider)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                        }}
                      >
                        <div
                          className="fm-avatar"
                          style={{ background: avatarColor(rider) }}
                        >
                          {initials(rider)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            <div className="fm-rider-name">
                              {rider.firstName} {rider.lastName}
                            </div>
                            <span className={`fm-badge fm-badge-${sCls}`}>
                              {rider.onlineStatus}
                            </span>
                          </div>
                          <div className="fm-rider-sub">
                            {carrierIcon(rider.carrierType)} {rider.carrierType}
                          </div>
                          {rider.currentActiveOrders > 0 && (
                            <span className="fm-badge fm-badge-order">
                              📦 {rider.currentActiveOrders} active order
                              {rider.currentActiveOrders > 1 ? "s" : ""}
                            </span>
                          )}
                          {loc && (
                            <div className="fm-rider-loc">
                              {locName ? (
                                `📍 ${locName}`
                              ) : (
                                <span style={{ color: colors.gray }}>
                                  📍 {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RiderTrackingMap;
